import { DYClient, TRIMMED_FIELDS, handleFromUrl, readDYIdentity } from '@theme/dy-core';

/**
 * One DY campaign's Choose result, keyed by campaign (selector) name in {@link DYProductRecsTabs#fetchChoices}.
 *
 * @typedef {object} DYChoiceResult
 * @property {Array<object>} slots
 * @property {string | null} decisionId
 * @property {object | null} analyticsMetadata
 * @property {string | null} campaignName
 */

/**
 * <dy-product-recs-tabs> — tabbed product recommendations, one Dynamic Yield campaign per tab.
 *
 * This is the DY-powered sibling of sections/product-recs-tabs.liquid. That section is built on
 * Shopify's *native* recommendations (`routes.product_recommendations_url` + the Section Rendering
 * API), so each tab is a Shopify "intent" (`related` / `complementary`) and one fetch returns a
 * whole rendered section. DY has no equivalent of an intent: its unit of merchandising is a named
 * campaign configured in the DY dashboard. So each tab here carries a campaign name instead, and
 * the fetch model is different — N per-product card fetches assembled client-side, exactly as
 * blocks/cart-recommendations.js and dy-plp.js already do.
 *
 * The render seam is the whole point. From DY's response this element reads ONLY
 * `productData.url` (→ handle) and `productData.group_id` (→ self-exclusion). It then fetches
 * `/products/{handle}?view=card` so Horizon renders its own product card server-side from real
 * product data. It never reads `image_url`, `price`, `Name`, brand or dropship fields out of the
 * DY payload — those columns are unreliable in the feed (live probe 19 Jul 2026: 6 of 8 slots
 * returned `image_url: "null"` and 7 of 8 returned `price: 0`, while all 8 resolved a valid
 * handle). Rendering from the handle means a broken feed column cannot become a broken card.
 *
 * Loading is two-phase, which differs deliberately from the native section (that one fetches both
 * tabs' full markup up front):
 *
 *   1. On viewport intersection, BOTH campaigns are asked for slots in parallel. These are two
 *      cheap JSON calls and they settle the tab-survival question immediately — a tab whose
 *      campaign returns nothing is removed then and there, before the shopper can click it.
 *   2. Card fetches (the expensive part — up to `max_products` requests per tab) run only for the
 *      tab actually being viewed. The second tab's cards are fetched on its first activation.
 *
 * Degradation is total and silent. A slot with no resolvable handle is skipped and its skeleton
 * removed; a tab whose campaign returns nothing is removed along with its panel; if no tab
 * survives, the element hides itself. Nothing broken, null or $0.00 is ever rendered.
 *
 * @element dy-product-recs-tabs
 */
class DYProductRecsTabs extends HTMLElement {
  /** Aborted on disconnect — covers the campaign calls and any in-flight card fetches. */
  #abortController = null;

  /** @type {import('@theme/dy-core').DYClient | null} */
  #client = null;

  /**
   * Per-tab state, index-aligned with the surviving `[role="tab"]` elements.
   * @type {Array<{
   *   slots: Array<object>,
   *   decisionId: string | null,
   *   rendered: boolean,
   *   controller: AbortController | null,
   * }>}
   */
  #tabState = [];

  /** @type {IntersectionObserver | null} */
  #intersectionObserver = null;

  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  connectedCallback() {
    // Re-create on every (re)connect so a reconnected element isn't left with a controller that a
    // previous disconnect already aborted.
    this.#abortController?.abort();
    this.#abortController = new AbortController();

    this.#client = this.dataset.apiKey
      ? new DYClient({
          apiKey: this.dataset.apiKey,
          dataCenter: this.dataset.dataCenter === 'eu' ? 'eu' : 'com',
          storeUrl: this.dataset.storeUrl,
          locale: this.dataset.locale || '',
        })
      : null;

    this.addEventListener('click', this.#handleTabClick);
    this.addEventListener('keydown', this.#handleTabKeydown);
    this.addEventListener('click', this.#handleSlotClick);

    this.#intersectionObserver = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0]?.isIntersecting) return;
        observer.disconnect();
        void this.#loadCampaigns();
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    this.#intersectionObserver.observe(this);
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#intersectionObserver?.disconnect();
    this.#intersectionObserver = null;
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;
    for (const state of this.#tabState) state.controller?.abort();
    this.#tabState = [];
    this.removeEventListener('click', this.#handleTabClick);
    this.removeEventListener('keydown', this.#handleTabKeydown);
    this.removeEventListener('click', this.#handleSlotClick);
    this.#client = null;
  }

  /** @returns {HTMLElement[]} */
  get #tabs() {
    return /** @type {HTMLElement[]} */ ([...this.querySelectorAll('[role="tab"]')]);
  }

  /**
   * Resolve the panel a tab controls. Uses `aria-controls` rather than index so the pairing
   * survives a tab being removed mid-flight.
   *
   * @param {HTMLElement} tab
   * @returns {HTMLElement | null}
   */
  #panelFor(tab) {
    const id = tab.getAttribute('aria-controls');
    if (!id) return null;
    // Scanning by property rather than a `#id` selector: the id is composed from section.id, and a
    // selector would need CSS.escape to be safe against any character Shopify puts there. This
    // avoids both the escaping question and the CSS.escape availability question entirely.
    for (const panel of this.querySelectorAll('[data-dy-recs-panel]')) {
      if (panel.id === id) return /** @type {HTMLElement} */ (panel);
    }
    return null;
  }

  /**
   * A tablist with a single tab is not a tab widget — it is a heading over a list, and exposing it
   * as `role="tablist"` misrepresents the structure to assistive tech. When only one tab is left —
   * whether pruned in phase 1 (empty campaign) or after a phase-2 render where every card fetch
   * failed — hide the chrome and strip the tab semantics so the surviving panel stands on its own.
   * Idempotent: a no-op unless exactly one tab remains.
   *
   * @returns {void}
   */
  #collapseToSingleTab() {
    const tabs = this.#tabs;
    if (tabs.length !== 1) return;
    const tablist = this.querySelector('[data-dy-recs-tablist]');
    if (tablist) /** @type {HTMLElement} */ (tablist).hidden = true;
    const panel = this.#panelFor(/** @type {HTMLElement} */ (tabs[0]));
    if (panel) {
      panel.removeAttribute('role');
      panel.removeAttribute('aria-labelledby');
    }
  }

  /**
   * Resolve once DY has established a user identity — i.e. `readDYIdentity()` (the same dy-core
   * helper the request builder uses) yields a `dyid` from `window.DY`, or the `_dyid` / `_dyid_server`
   * cookie. Gating
   * on the very function that fills the request body means the gate and the payload can never
   * disagree.
   *
   * Why this exists: on a large viewport this element's IntersectionObserver (400px rootMargin) can
   * fire {@link #loadCampaigns} before the DY embed's cookie-sync has run, so the grouped Choose
   * ships with empty `user`/`session`. Item-to-item campaigns (Related) still answer, but
   * user-affinity campaigns (Complementary) return zero slots — the tab is then pruned and, if it
   * was the last survivor, the tablist collapses to nothing. Live-proven on Mimco AU (4 Aug 2026):
   * the identical Capri choose returned Complementary 0 slots with empty identity and 7 slots once
   * `dyid`/`session` were sent. A smaller viewport hides the bug only because the section sits below
   * the fold, so its choose fires after identity already exists.
   *
   * Bounded by `timeoutMs`: a blocked, disabled or slow DY embed must never hang the section — after
   * the cap we proceed anyway, yielding at least the anonymous Related tab. The skeletons stay
   * visible throughout, so the wait reads as ordinary loading rather than a stall.
   *
   * @param {AbortSignal} signal
   * @param {number} [timeoutMs=3000]
   * @returns {Promise<void>}
   */
  #waitForDYIdentity(signal, timeoutMs = 3000) {
    if (signal.aborted || readDYIdentity().user.dyid != null) return Promise.resolve();
    return new Promise((resolve) => {
      let settled = false;
      /** @type {ReturnType<typeof setInterval> | undefined} */
      let poll;
      /** @type {ReturnType<typeof setTimeout> | undefined} */
      let timer;
      const done = () => {
        if (settled) return;
        settled = true;
        clearInterval(poll);
        clearTimeout(timer);
        signal.removeEventListener('abort', done);
        resolve();
      };
      poll = setInterval(() => {
        if (readDYIdentity().user.dyid != null) done();
      }, 120);
      timer = setTimeout(done, timeoutMs);
      signal.addEventListener('abort', done, { once: true });
    });
  }

  /**
   * Phase 1 — ask every tab's campaign for slots in parallel, prune the tabs that came back empty,
   * then render cards for whichever tab is active.
   *
   * @returns {Promise<void>}
   */
  async #loadCampaigns() {
    const signal = this.#abortController?.signal;
    if (!this.#client || !signal) {
      this.hidden = true;
      return;
    }

    // DY's PRODUCT context takes the SKU as it appears in the feed. The live slot payload on these
    // stores reports the selected variant id, so prefer it and fall back to the product id.
    const contextData = [this.dataset.variantId || this.dataset.productId].filter(Boolean);
    const maxProducts = Number(this.dataset.maxProducts) || 4;
    const currentProductId = String(this.dataset.productId || '');

    const tabs = this.#tabs;
    const campaigns = tabs.map((tab) => (tab.dataset.dyCampaign || '').trim());

    // Gate the choose on DY identity being established. On a large viewport this element's
    // IntersectionObserver can fire before the DY embed's cookie-sync has run, so the request would
    // ship with empty user/session — see #waitForDYIdentity. The skeletons stay visible meanwhile.
    await this.#waitForDYIdentity(signal);
    if (signal.aborted) return;

    const byCampaign = await this.#fetchChoices([...new Set(campaigns.filter(Boolean))], contextData, signal);
    if (signal.aborted) return;

    /** @type {HTMLElement[]} */
    const survivingTabs = [];
    this.#tabState = [];

    tabs.forEach((tab, index) => {
      // Keyed by campaign name, so if a merchant misconfigures both tabs to the SAME campaign both
      // resolve to the same slots (identical products in two tabs). Left as-is by design: that is an
      // immediately visible cue to fix the setting, not a failure state worth code to mask.
      const result = byCampaign.get(campaigns[index]) ?? null;
      const slots = (result?.slots ?? [])
        // Never recommend the product being viewed. The campaign should exclude it itself, but a
        // session predating that campaign change may still include it.
        .filter((slot) => String(slot.productData?.group_id ?? '') !== currentProductId)
        .slice(0, maxProducts);

      const panel = this.#panelFor(tab);
      if (!slots.length) {
        // A tab that leads nowhere is worse than no tab: it is a visible affordance that resolves
        // to a dead end. Remove it and its panel outright rather than showing an empty state.
        tab.remove();
        panel?.remove();
        return;
      }

      if (panel && result?.analyticsMetadata) {
        // Provenance for debugging and for anyone reconciling theme-rendered recs against DY's
        // own reporting. Not read back by this element.
        panel.dataset.dyCampaignId = String(result.analyticsMetadata.campaignId ?? '');
        panel.dataset.dyCampaignName = result.campaignName ?? result.analyticsMetadata.campaignName ?? '';
      }

      survivingTabs.push(tab);
      this.#tabState.push({ slots, decisionId: result?.decisionId ?? null, rendered: false, controller: null });
    });

    if (!survivingTabs.length) {
      this.hidden = true;
      return;
    }

    this.#collapseToSingleTab();

    // Whichever tab is selected server-side (the first, normally) may have just been pruned —
    // make sure exactly one surviving tab is active before rendering.
    const active = survivingTabs.find((tab) => tab.getAttribute('aria-selected') === 'true');
    this.#setActiveTab(active ?? /** @type {HTMLElement} */ (survivingTabs[0]));
  }

  /**
   * Ask DY for every tab's campaign in ONE request and return the choices keyed by campaign name.
   *
   * The Choose API accepts `selector.names` as an array and answers with one `choice` per selector,
   * each carrying its own `decisionId` and `analyticsMetadata`. Two tabs therefore cost one HTTP
   * request, not two — which matters because DY bills per API request and, as measured on 19 Jul
   * 2026, the endpoint **ignores the productData field filter**: every slot came back with all 38
   * feed columns regardless of `options.productData.fieldFilter`. `TRIMMED_FIELDS` is still sent
   * (it is free and correct if DY ever honours it), but the payload is large, so halving the number
   * of requests is the only lever that actually works. Across five brands this is a real cost line.
   *
   * IMPORTANT (live-verified 19 Jul 2026, Mimco AU): the DY **selector-group** name is NOT a valid
   * selector. Sending `selector.names: ["PDP Recommendations"]` — the group containing both
   * campaigns — returns HTTP 200 with `warnings: [{code: "W001", message: "selector name \"PDP
   * Recommendations\" did not match any campaign"}]` and `choices: []`. Sending the two campaign
   * names explicitly, `["PDP | Related", "PDP | Complementary"]`, returns 200 with no warnings and
   * two populated choices. So the grouping benefit comes from the names array, not the group name;
   * do not "simplify" this to the group name.
   *
   * Choices are matched back to tabs by `choice.name` (which echoes the requested selector name
   * exactly, pipes and spaces included) — never by array position, which DY does not guarantee.
   *
   * `client.choose()` cannot be used for this: it unwraps via `unwrap()`, which reads `choices[0]`
   * only and discards `analyticsMetadata`. We therefore build the request with the client's own
   * builder and read the envelope ourselves. When that builder isn't present on the installed
   * dy-core build, we fall back to one `choose()` per campaign — correct, just twice the requests
   * and no provenance metadata.
   *
   * @param {string[]} names - Unique campaign (selector) names.
   * @param {string[]} contextData - DY PRODUCT page context (variant or product id).
   * @param {AbortSignal} signal
   * @returns {Promise<Map<string, DYChoiceResult>>}
   */
  async #fetchChoices(names, contextData, signal) {
    /** @type {Map<string, DYChoiceResult>} */
    const out = new Map();
    if (!this.#client || !names.length) return out;

    const overrides = {
      location: window.location.href,
      // Only `url` (render seam) and `group_id` (self-exclusion) are ever read from a slot.
      fields: TRIMMED_FIELDS,
    };

    const client = this.#client;
    if (typeof client.buildChooseRequest === 'function' && typeof client.chooseEndpoint === 'string') {
      try {
        const response = await fetch(client.chooseEndpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'DY-API-Key': this.dataset.apiKey ?? '' },
          body: JSON.stringify(client.buildChooseRequest(overrides, names, 'PRODUCT', contextData)),
          signal,
        });
        if (!response.ok) throw new Error(`choose ${response.status}`);
        const json = await response.json();
        // A campaign that matched nothing is reported as a 200 + W001 warning with no choice, not
        // an error. Echo it for debugging (a misspelt campaign name is otherwise silent) and let
        // the missing choice hide that tab.
        for (const warning of json.warnings ?? []) {
          console.warn('[dy-product-recs-tabs] DY warning:', warning?.code, warning?.message);
        }
        for (const choice of json.choices ?? []) {
          const variation = choice?.variations?.[0];
          if (!choice?.name || !variation?.payload?.data) continue;
          out.set(choice.name, {
            slots: variation.payload.data.slots ?? [],
            decisionId: choice.decisionId ?? null,
            analyticsMetadata: variation.analyticsMetadata ?? null,
            campaignName: choice.name,
          });
        }
        return out;
      } catch (error) {
        if (signal.aborted) return out;
        console.warn('[dy-product-recs-tabs] grouped choose failed, falling back per campaign:', error);
      }
    }

    const results = await Promise.all(
      names.map(async (name) => {
        try {
          return await client.choose(name, 'PRODUCT', contextData, overrides);
        } catch (error) {
          console.warn('[dy-product-recs-tabs] campaign failed:', name, error);
          return null;
        }
      })
    );
    names.forEach((name, index) => {
      const result = results[index];
      if (!result) return;
      out.set(name, {
        slots: result.slots ?? [],
        decisionId: result.decisionId ?? null,
        analyticsMetadata: null,
        campaignName: name,
      });
    });
    return out;
  }

  /**
   * Phase 2 — swap a panel's skeletons for real Horizon cards. Runs once per tab, on first view.
   *
   * @param {number} index - Index into `#tabState`, aligned with the surviving tabs.
   * @returns {Promise<void>}
   */
  async #renderTab(index) {
    const state = this.#tabState[index];
    const tab = this.#tabs[index];
    if (!state || !tab || state.rendered) return;

    const panel = this.#panelFor(tab);
    const grid = panel?.querySelector('[data-dy-recs-grid]');
    if (!grid) return;

    // Guard against a second concurrent pass for the same tab (e.g. rapid re-activation before the
    // fetches resolve) WITHOUT marking the tab rendered. `rendered` is reserved to mean "completed"
    // — set only after a successful swap below — so the abort-on-switch-away check in #setActiveTab
    // (`!state.rendered`) can still see a mid-fetch tab as cancellable.
    if (state.controller) return;

    // Scoped to this tab so switching away aborts its in-flight card fetches; also chained off the
    // element-wide controller so disconnect aborts everything. The `signal: controller.signal`
    // removal option auto-detaches this listener when the per-tab controller is aborted (a
    // switch-away), so rapidly flipping between tabs doesn't accumulate abort listeners on the
    // long-lived element controller.
    const controller = new AbortController();
    state.controller = controller;
    this.#abortController?.signal.addEventListener('abort', () => controller.abort(), {
      once: true,
      signal: controller.signal,
    });

    // Expose a busy state while the cards are fetched. The skeletons are aria-hidden, so without
    // this a screen-reader user landing on the panel mid-fetch would meet an apparently empty region
    // with no indication that content is on its way. Cleared at every exit below.
    grid.setAttribute('aria-busy', 'true');

    let swappedCount = 0;
    try {
      swappedCount = await this.#swapSkeletons(grid, state, controller.signal);
    } catch (error) {
      if (controller.signal.aborted) {
        // Aborted by a switch-away: clear the controller so re-activating starts a fresh pass.
        state.controller = null;
        grid.removeAttribute('aria-busy');
        return;
      }
      console.warn('[dy-product-recs-tabs] card render failed:', error);
    }
    if (controller.signal.aborted) {
      state.controller = null;
      grid.removeAttribute('aria-busy');
      return;
    }
    state.controller = null;
    grid.removeAttribute('aria-busy');

    if (swappedCount === 0) {
      // Every card fetch failed — same reasoning as an empty campaign: drop the dead tab.
      // If the user keyboarded to this tab it is likely the focused element; removing it would drop
      // focus to <body>, so remember that and restore focus to a surviving target afterwards.
      const hadFocus = tab === document.activeElement || tab.contains(document.activeElement);
      tab.remove();
      panel?.remove();
      this.#tabState.splice(index, 1);
      const remaining = this.#tabs;
      if (!remaining.length) {
        this.hidden = true;
        return;
      }
      // The prune may have left a single tab — collapse the now-misleading tablist chrome, matching
      // the phase-1 treatment in #loadCampaigns.
      this.#collapseToSingleTab();
      const nextActive = /** @type {HTMLElement} */ (remaining[0]);
      this.#setActiveTab(nextActive);
      if (hadFocus) {
        // Prefer the surviving tab, but if the collapse just hid the tablist that tab is no longer
        // focusable — fall back to its panel (made programmatically focusable) so focus stays inside
        // the component rather than resetting to the document body.
        const tablist = this.querySelector('[data-dy-recs-tablist]');
        if (tablist && !(/** @type {HTMLElement} */ (tablist).hidden)) {
          nextActive.focus();
        } else {
          const survivingPanel = this.#panelFor(nextActive);
          if (survivingPanel) {
            survivingPanel.setAttribute('tabindex', '-1');
            survivingPanel.focus();
          }
        }
      }
      return;
    }

    state.rendered = true;

    // Force the live region to re-announce even for the second tab's lazy load: clear first, then
    // set on the next frame. A same-string overwrite is a no-op most screen readers won't announce,
    // so an empty-then-fill mutation is what actually gets spoken.
    const status = this.querySelector('[data-dy-recs-status]');
    if (status) {
      status.textContent = '';
      const message = this.dataset.statusLoaded ?? '';
      requestAnimationFrame(() => {
        status.textContent = message;
      });
    }

    if (this.dataset.layoutType === 'carousel') this.#setUpCarouselControls(panel);
    this.#updateArrowState(panel);
  }

  /**
   * Fetch each slot's card through the render seam and swap it into the matching skeleton `<li>`
   * (in DOM order). Any skeleton left over once slots run out is removed, so a short response
   * never leaves stuck shimmer placeholders.
   *
   * A slot whose `url` is missing or unparseable simply yields no card and its skeleton is
   * removed — no placeholder image, no zero price.
   *
   * @param {Element} grid
   * @param {{slots: Array<object>, decisionId: string | null}} state
   * @param {AbortSignal} signal
   * @returns {Promise<number>} Count of skeletons successfully swapped for a real card.
   */
  async #swapSkeletons(grid, state, signal) {
    const root = window.Shopify?.routes?.root ?? '/';
    const parser = new DOMParser();
    const skeletons = [...grid.querySelectorAll('[data-dy-recs-skeleton]')];

    const cards = await Promise.all(
      state.slots.map(async (slot) => {
        const handle = handleFromUrl(slot.productData?.url ?? '');
        if (!handle) return null;
        try {
          const response = await fetch(`${root}products/${handle}?view=card`, { signal });
          if (!response.ok) return null;
          const doc = parser.parseFromString(await response.text(), 'text/html');
          const section = doc.querySelector('#shopify-section-dy-product-card') ?? doc.body;
          const card = section?.querySelector('.product-card');
          if (!card) return null;
          return { card, slotId: slot.slotId ?? null };
        } catch {
          return null;
        }
      })
    );
    if (signal.aborted) return 0;

    let swappedCount = 0;
    skeletons.forEach((skeleton, index) => {
      const item = cards[index];
      if (!item) {
        skeleton.remove();
        return;
      }
      skeleton.replaceChildren(document.importNode(item.card, true));
      skeleton.removeAttribute('aria-hidden');
      skeleton.classList.remove('dy-product-recs-tabs__skeleton');
      delete /** @type {HTMLElement} */ (skeleton).dataset.dyRecsSkeleton;
      if (item.slotId) {
        /** @type {HTMLElement} */ (skeleton).dataset.dySlotId = item.slotId;
        if (state.decisionId) /** @type {HTMLElement} */ (skeleton).dataset.dyDecisionId = state.decisionId;
      }
      swappedCount += 1;
    });

    return swappedCount;
  }

  /**
   * Activate a tab: sync `aria-selected` / `tabindex` / panel visibility, abort the outgoing
   * tab's in-flight card fetches, and render the incoming tab if it hasn't been rendered yet.
   *
   * @param {HTMLElement} tab
   * @returns {void}
   */
  #setActiveTab(tab) {
    const tabs = this.#tabs;
    const index = tabs.indexOf(tab);
    if (index === -1) return;

    tabs.forEach((candidate, candidateIndex) => {
      const isActive = candidate === tab;
      candidate.setAttribute('aria-selected', isActive ? 'true' : 'false');
      candidate.setAttribute('tabindex', isActive ? '0' : '-1');
      const panel = this.#panelFor(candidate);
      if (panel) panel.toggleAttribute('hidden', !isActive);
      // Switching away from a tab whose cards are still arriving: drop those fetches. The tab is
      // marked unrendered so re-activating it starts a fresh, complete pass.
      if (!isActive) {
        const state = this.#tabState[candidateIndex];
        if (state && !state.rendered) state.controller?.abort();
      }
    });

    void this.#renderTab(index);
  }

  /**
   * @param {Event} e
   * @returns {void}
   */
  #handleTabClick = (e) => {
    const tab = /** @type {HTMLElement | null} */ (
      /** @type {Element} */ (e.target)?.closest('[role="tab"]')
    );
    if (!tab || !this.contains(tab)) return;
    this.#setActiveTab(tab);
  };

  /**
   * Arrow / Home / End navigation within the tablist, per the ARIA tabs pattern.
   *
   * @param {KeyboardEvent} e
   * @returns {void}
   */
  #handleTabKeydown = (e) => {
    const tabs = this.#tabs;
    const index = tabs.indexOf(/** @type {HTMLElement} */ (e.target));
    if (index === -1) return;

    let next;
    if (e.key === 'ArrowRight') next = tabs[(index + 1) % tabs.length];
    if (e.key === 'ArrowLeft') next = tabs[(index - 1 + tabs.length) % tabs.length];
    if (e.key === 'Home') next = tabs[0];
    if (e.key === 'End') next = tabs[tabs.length - 1];
    if (!next) return;

    e.preventDefault();
    next.focus();
    this.#setActiveTab(next);
  };

  /**
   * Wires the active panel's prev/next buttons to scroll its grid by one viewport width, and keeps
   * their disabled state in sync with scroll position (card swaps change the scrollable width).
   *
   * @param {Element | null} panel
   * @returns {void}
   */
  #setUpCarouselControls(panel) {
    const grid = panel?.querySelector('[data-dy-recs-grid]');
    const prev = panel?.querySelector('[data-dy-recs-prev]');
    const next = panel?.querySelector('[data-dy-recs-next]');
    if (!grid || !prev || !next || panel?.dataset.dyCarouselReady === 'true') return;

    /** @type {HTMLElement} */ (panel).dataset.dyCarouselReady = 'true';
    // Honour prefers-reduced-motion: skip the smooth-scroll animation for users who opt out, matching
    // the reduced-motion guard on this component's shimmer keyframes.
    const scrollByViewport = (direction) => {
      const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
      grid.scrollBy({ left: direction * grid.clientWidth, behavior: reduceMotion ? 'auto' : 'smooth' });
    };
    prev.addEventListener('click', () => scrollByViewport(-1));
    next.addEventListener('click', () => scrollByViewport(1));
    grid.addEventListener('scroll', () => this.#updateArrowState(panel), { passive: true });

    this.#resizeObserver ??= new ResizeObserver((entries) => {
      for (const entry of entries) this.#updateArrowState(entry.target.closest('[data-dy-recs-panel]'));
    });
    this.#resizeObserver.observe(grid);
  }

  /**
   * Disables the previous button at the start of scroll and the next button at the end.
   *
   * @param {Element | null} panel
   * @returns {void}
   */
  #updateArrowState = (panel) => {
    const grid = panel?.querySelector('[data-dy-recs-grid]');
    const prev = panel?.querySelector('[data-dy-recs-prev]');
    const next = panel?.querySelector('[data-dy-recs-next]');
    if (!grid || !prev || !next) return;

    const maxScroll = grid.scrollWidth - grid.clientWidth;
    prev.toggleAttribute('disabled', grid.scrollLeft <= 0);
    next.toggleAttribute('disabled', grid.scrollLeft >= maxScroll - 1);
  };

  /**
   * Delegated click handler on the cards. Fires the mandatory SLOT_CLICK engagement before
   * navigating, mirroring dy-plp.js and cart-recommendations.js — the keepalive fetch survives
   * the navigation, so no artificial delay is needed.
   *
   * @param {MouseEvent} e
   * @returns {void}
   */
  #handleSlotClick = (e) => {
    if (e.ctrlKey || e.metaKey || e.shiftKey || e.altKey || e.button !== 0) return;
    if (/** @type {Element} */ (e.target)?.closest('button, [role="button"], form, input, label, select')) return;

    const li = /** @type {Element | null} */ (
      /** @type {Element} */ (e.target)?.closest('.dy-product-recs-tabs__item[data-dy-slot-id]')
    );
    if (!li) return;

    const slotId = /** @type {HTMLElement} */ (li).dataset.dySlotId;
    if (!slotId) return;

    // Prefer the per-card decision id (stamped at swap time) so a click always reports against the
    // decision that produced this card — each tab has its own decision.
    const decisionId = /** @type {HTMLElement} */ (li).dataset.dyDecisionId || null;

    const targetLink = /** @type {Element | null} */ (/** @type {Element} */ (e.target)?.closest('a'));
    if (targetLink && !/** @type {HTMLAnchorElement} */ (targetLink).href?.includes('/products/')) return;

    const href =
      /** @type {HTMLAnchorElement} */ (targetLink)?.href ||
      /** @type {HTMLAnchorElement | null} */ (li.querySelector('a[href*="/products/"]'))?.href;
    if (!href) return;

    e.preventDefault();
    this.#client?.reportSlotClick({ slotId }, decisionId);
    delete /** @type {HTMLElement} */ (li).dataset.dySlotId;
    window.location.href = href;
  };
}

if (!customElements.get('dy-product-recs-tabs')) {
  customElements.define('dy-product-recs-tabs', DYProductRecsTabs);
}
